import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { skillCallTemplate } from '@/lib/skill-call-template';
import type { Skill, ToolVisibility } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Code2, MoreHorizontal, Plus, Sparkles, Type } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VISIBILITY_LABEL: Record<ToolVisibility, string> = {
  summary: '요약',
  hidden: '숨김',
  detailed: '상세',
};

const VISIBILITY_CLASS: Record<ToolVisibility, string> = {
  summary: 'bg-surface-muted text-foreground-muted',
  hidden: 'bg-[hsl(30_90%_50%)]/15 text-[hsl(30_90%_50%)]',
  detailed: 'bg-[hsl(210_90%_55%)]/15 text-[hsl(210_90%_55%)]',
};

export function AdminSkillsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: () => api.adminSkillList(),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.adminSkillUpdate(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'skills'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminSkillDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'skills'] }),
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    },
  });

  const skills = data?.skills ?? [];

  const normalizedSearch = search.trim().toLowerCase();
  const filteredSkills = normalizedSearch
    ? skills.filter(
        (s) =>
          s.name.toLowerCase().includes(normalizedSearch) ||
          s.slug.toLowerCase().includes(normalizedSearch) ||
          (s.description?.toLowerCase().includes(normalizedSearch) ?? false),
      )
    : skills;

  return (
    <PageShell
      header={
        <PageHeader
          title="Skill"
          count={filteredSkills.length}
          description="LLM 에 부착되는 시스템 프롬프트 + 도구 번들. 사용자가 챗 세션에서 골라 쓸 수 있습니다."
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/admin/skills/create-with-ai' })}
              >
                <Sparkles className="size-4" />
                AI 로 만들기
              </Button>
              <Button
                onClick={() =>
                  navigate({
                    to: '/admin/skills/$id',
                    params: { id: 'new' },
                    search: { dataSourceId: undefined },
                  })
                }
              >
                <Plus className="size-4" />
                Skill 추가
              </Button>
            </div>
          }
        />
      }
    >
      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : 'Skill 목록을 불러오지 못했습니다.'}
        </p>
      ) : (
        <>
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="이름, slug, 설명으로 검색"
              className="h-9 w-72 rounded-md border border-border bg-surface px-3 text-sm"
            />
          </div>
          <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
                <tr>
                  <Th>slug / 이름</Th>
                  <Th>툴 노출</Th>
                  <Th>툴 갯수</Th>
                  <Th>상태</Th>
                  <Th>업데이트</Th>
                  <Th align="right">액션</Th>
                </tr>
              </thead>
              <tbody>
                {filteredSkills.map((s) => (
                  <Row
                    key={s.id}
                    skill={s}
                    onEdit={() =>
                      navigate({
                        to: '/admin/skills/$id',
                        params: { id: s.id },
                        search: { dataSourceId: undefined },
                      })
                    }
                    onToggleEnabled={() => updateMut.mutate({ id: s.id, enabled: !s.enabled })}
                    onDelete={() => {
                      if (
                        window.confirm(
                          `Skill "${s.name}" 을 삭제할까요?\n이 Skill 을 쓰던 세션은 skillId 가 해제됩니다.`,
                        )
                      ) {
                        deleteMut.mutate(s.id);
                      }
                    }}
                  />
                ))}
                {skills.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-foreground-muted py-10">
                      등록된 Skill 이 없습니다.
                    </td>
                  </tr>
                ) : filteredSkills.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-foreground-muted py-10">
                      검색 결과가 없습니다.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function Row({
  skill,
  onEdit,
  onToggleEnabled,
  onDelete,
}: {
  skill: Skill;
  onEdit: () => void;
  onToggleEnabled: () => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-t border-border hover:bg-surface-muted/30">
      <td className="px-4 py-3">
        <div className="flex items-start gap-1.5">
          <button type="button" onClick={onEdit} className="text-left hover:underline">
            <div className="font-medium">{skill.name}</div>
            <div className="mt-0.5 font-mono text-[11px] text-foreground-muted">{skill.slug}</div>
          </button>
          <div className="flex items-center gap-0.5 pt-0.5">
            <CopyButton icon={Type} text={skill.name} label="이름 복사" />
            <CopyButton icon={Code2} text={skillCallTemplate(skill.id)} label="skill 함수 복사" />
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
            VISIBILITY_CLASS[skill.toolVisibility],
          )}
        >
          {VISIBILITY_LABEL[skill.toolVisibility]}
        </span>
      </td>
      <td className="px-4 py-3 text-foreground-muted">{skill.tools.length}</td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={onToggleEnabled}
          className={cn(
            'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
            skill.enabled
              ? 'bg-success/15 text-success hover:bg-success/25'
              : 'bg-surface-muted text-foreground-muted hover:bg-surface-muted/70',
          )}
        >
          {skill.enabled ? '활성' : '비활성'}
        </button>
      </td>
      <td className="px-4 py-3 text-foreground-muted text-xs">{formatRelative(skill.updatedAt)}</td>
      <td className="px-4 py-3 text-right">
        <RowMenu
          onEdit={onEdit}
          onToggleEnabled={onToggleEnabled}
          enabled={skill.enabled}
          onDelete={onDelete}
        />
      </td>
    </tr>
  );
}

const ROW_MENU_HEIGHT = 140;
const ROW_MENU_MARGIN = 8;

function RowMenu({
  onEdit,
  onToggleEnabled,
  enabled,
  onDelete,
}: {
  onEdit: () => void;
  onToggleEnabled: () => void;
  enabled: boolean;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const right = window.innerWidth - rect.right;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < ROW_MENU_HEIGHT + ROW_MENU_MARGIN) {
        setPos({ bottom: window.innerHeight - rect.top + 4, right });
      } else {
        setPos({ top: rect.bottom + 4, right });
      }
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{
                position: 'fixed',
                right: pos.right,
                ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
              }}
              className="w-56 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm text-left"
            >
              <MenuItem
                onClick={() => {
                  close();
                  onEdit();
                }}
              >
                편집
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onToggleEnabled();
                }}
              >
                {enabled ? '비활성화' : '활성화'}
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem
                danger
                onClick={() => {
                  close();
                  onDelete();
                }}
              >
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'text-destructive' : '',
      )}
    >
      {children}
    </button>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR');
}
